import React, {ReactElement, ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {ClickAwayListener, Popper as MuiPopper, PopperPlacementType} from '@mui/base';
import clsx from 'clsx';

export interface PopperProps {
  placement?: PopperPlacementType;
  tooltip: ReactNode;
  children: ReactElement;
  container?: Element | null;
  className?: string;
  hoverClassName?: string;
  marginLeft?: number;
  isOpen?: boolean;
  onOpen?: () => void;
  onHide?: () => void;
}

function Popper({
  placement = undefined,
  tooltip,
  children,
  container = null,
  className = undefined,
  hoverClassName = undefined,
  marginLeft = 0,
  isOpen = false,
  onOpen,
  onHide,
}: PopperProps): JSX.Element {
  const [isHovered, setIsHovered] = useState(isOpen);
  const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>(null);
  const childRef = useRef<HTMLElement | null>(null);

  function toggle() {
    if (!isHovered) {
      onOpen && onOpen();
    }
    setIsHovered(!isHovered);
  }

  const hide = useCallback(() => {
    setIsHovered(false);
    onHide && onHide();
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', hide);

    return () => {
      window.removeEventListener('scroll', hide);
    };
  }, [hide]);

  return (
    <>
      <ClickAwayListener
        touchEvent="onTouchStart"
        onClickAway={() => hide()}
      >
        {React.cloneElement(children, {
          ref: childRef,
          onTouchEnd: toggle,
          className: clsx(isHovered && hoverClassName, children.props.className),
        })}
      </ClickAwayListener>
      <MuiPopper
        disablePortal
        open={isHovered}
        anchorEl={childRef.current}
        container={container}
        placement={placement}
        slotProps={{root: {className}}}
        modifiers={[
          {
            name: 'arrow',
            enabled: true,
            options: {element: arrowRef},
          },
          {
            name: 'offset',
            options: {
              offset: [0, 13],
            },
          },
          {
            name: 'flip',
            enabled: false,
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              padding: 20,
            },
          },
        ]}
      >
        {tooltip}
        <div
          className="tooltip_arrow"
          style={{marginLeft: `${marginLeft}px`}}
          ref={setArrowRef}
        />
      </MuiPopper>
    </>
  );
}

export default Popper;
